import React, { useMemo, useState } from 'react';
import { useApp } from '@/context/AppContext';
import { apiFetch } from '@/lib/supabaseClient';
import { Link } from 'react-router-dom';
import FerieAnalisi from '@/pages/FerieAnalisi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, Check, X, Calendar, AlertTriangle, ChevronRight, Ban, CheckCircle2, Search } from 'lucide-react';
import { toast } from 'sonner';
const STATO_BADGE = {
  richiesta:    { txt: 'Richiesta',   cls: 'bg-amber-50 text-amber-800' },
  approvata:    { txt: 'Approvata',   cls: 'bg-emerald-50 text-brand-green' },
  programmata:  { txt: 'Programmata', cls: 'bg-blue-50 text-blue-800' },
  goduta:       { txt: 'Goduta',      cls: 'bg-stone-100 text-stone-700' },
  rifiutata:    { txt: 'Rifiutata',   cls: 'bg-red-50 text-red-700' },
  annullata:    { txt: 'Annullata',   cls: 'bg-stone-100 text-stone-500 line-through' },
};

function StatoBadge({ stato }) {
  const s = STATO_BADGE[stato] || STATO_BADGE.richiesta;
  return <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${s.cls}`}>{s.txt}</span>;
}

export default function Ferie({ operationalView = null }) {
  const {
    dipendentiInScope, saldoOf, periodi, canApprove, ANNO_CORRENTE, ferieConfig,
    reloadDataset,
    effectiveRoleId, scope, hydrated, hydrationError,
  } = useApp();
  // Show technical hydration indicators only when DEV tools are on (Preview/DEV).
  const showDevIndicators = process.env.REACT_APP_SHOW_DEV_TOOLS !== 'false';
  const [openReq, setOpenReq] = useState(false);
  const [analysisRefreshKey, setAnalysisRefreshKey] = useState(0);

  const refreshLiveData = async () => {
    await reloadDataset();
    setAnalysisRefreshKey((value) => value + 1);
  };

  const updatePeriodStatus = async (id, stato, message) => {
    try {
      await apiFetch(`/api/ferie/analisi/periodi/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ stato }),
      });
      await refreshLiveData();
      toast.success(message);
    } catch (error) {
      toast.error(error.message || 'Aggiornamento non riuscito');
    }
  };

  // Richieste visibili per lo scope
  const periodiInScope = useMemo(() => {
    const ids = new Set(dipendentiInScope.map(d => d.id));
    return periodi.filter(p => ids.has(p.dipendenteId));
  }, [periodi, dipendentiInScope]);

  const richieste = periodiInScope.filter(p => p.stato === 'richiesta');
  const programmate = periodiInScope.filter(p => ['programmata','approvata'].includes(p.stato));
  const godute = periodiInScope.filter(p => p.stato === 'goduta');

  const richiesteSection = (
    <section className="space-y-4 rounded-2xl border border-app-border bg-app-surface p-4 sm:p-5" data-testid="ferie-richieste-section">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green mb-1">Gestione operativa</div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-brand-navy">Richieste</h2>
        <p className="mt-1 text-sm text-stone-500">Gestisci le richieste ferie dei dipendenti.</p>
      </div>
      <div className="space-y-4">
        <ListaPeriodi
          titolo="Da approvare"
          periodi={richieste}
          dipendentiInScope={dipendentiInScope}
          saldoOf={saldoOf}
          azioni={canApprove ? [
            { key: 'approva', label: 'Approva', testid: 'btn-approva', cls: 'bg-brand-green hover:bg-brand-green-hover text-white', icon: Check, fn: (id) => updatePeriodStatus(id, 'programmata', 'Approvata → programmata') },
            { key: 'rifiuta', label: 'Rifiuta', testid: 'btn-rifiuta', cls: 'border border-stone-200 text-red-700 hover:bg-red-50', variant: 'outline', icon: X, fn: (id) => updatePeriodStatus(id, 'rifiutata', 'Rifiutata') },
          ] : []}
        />
        <ListaPeriodi
          titolo="Programmate"
          periodi={programmate}
          dipendentiInScope={dipendentiInScope}
          saldoOf={saldoOf}
          azioni={canApprove ? [
            { key: 'goduta', label: 'Segna goduta', testid: 'btn-goduta', cls: 'bg-stone-800 hover:bg-stone-900 text-white', icon: CheckCircle2, fn: (id) => updatePeriodStatus(id, 'goduta', 'Segnata come goduta') },
            { key: 'annulla', label: 'Annulla', variant: 'outline', cls: 'border border-stone-200 text-stone-700 hover:bg-stone-50', icon: Ban, fn: (id) => updatePeriodStatus(id, 'annullata', 'Annullata') },
          ] : []}
        />
      </div>
    </section>
  );

  const storicoSection = (
    <section className="space-y-4 rounded-2xl border border-app-border bg-app-surface p-4 sm:p-5" data-testid="ferie-storico-section">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green mb-1">Gestione operativa</div>
        <h2 className="font-display text-xl font-semibold tracking-tight text-brand-navy">Storico</h2>
        <p className="mt-1 text-sm text-stone-500">Consulta lo storico delle ferie e delle operazioni.</p>
      </div>
      <div className="space-y-4">
        <ListaPeriodi
          titolo="Ferie godute (anno corrente)"
          periodi={godute}
          dipendentiInScope={dipendentiInScope}
          saldoOf={saldoOf}
        />
        <div className="rounded-2xl border border-stone-200 bg-white p-5">
          <div className="font-display font-semibold text-stone-900 tracking-tight">Storico anni precedenti</div>
          <p className="mt-1 text-xs text-stone-500">Apri il dettaglio del dipendente per vedere gli anni chiusi e i riporti.</p>
        </div>
      </div>
    </section>
  );

  if (operationalView) {
    const isHistory = operationalView === 'storico';
    return (
      <div className="space-y-6 pb-20 md:pb-0" data-testid={isHistory ? 'ferie-storico-page' : 'ferie-richieste-page'}>
        <Link to="/ferie" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="ferie-page-back">
          <ChevronRight className="h-4 w-4 rotate-180" /> Torna a Ferie
        </Link>
        <header>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green mb-1">Ferie · gestione operativa</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-brand-navy md:text-4xl">{isHistory ? 'Storico ferie' : 'Richieste ferie'}</h1>
          <p className="mt-2 text-sm text-stone-500">{isHistory ? 'Consulta lo storico delle ferie e delle operazioni.' : 'Gestisci le richieste ferie dei dipendenti.'}</p>
        </header>
        {isHistory ? storicoSection : richiesteSection}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-green mb-2">Ferie · Anno {ANNO_CORRENTE}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-brand-navy">Gestione ferie</h1>
          <p className="mt-2 text-stone-500 text-sm" data-testid="ferie-header-info">
            {dipendentiInScope.length} dipendenti nel tuo scope
            {showDevIndicators && hydrated && !hydrationError && (
              <> · <span className="text-amber-800 font-medium" data-testid="ferie-datasource-real">dataset reale importato</span></>
            )}
            {showDevIndicators && hydrationError && (
              <> · <span className="text-red-700 font-medium" data-testid="ferie-datasource-error">dataset non disponibile · fallback locale</span></>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <Dialog open={openReq} onOpenChange={setOpenReq}>
          <DialogTrigger asChild>
            <Button data-testid="btn-nuova-richiesta" className="rounded-full gap-2 bg-brand-green hover:bg-brand-green-hover">
              <Plus className="w-4 h-4"/> Nuova richiesta
            </Button>
          </DialogTrigger>
          <RichiediFerieDialog
            defaultDipendenteId={effectiveRoleId === 'dipendente' ? scope.dipendenteId : dipendentiInScope[0]?.id}
            forzaSelf={effectiveRoleId === 'dipendente'}
            onClose={() => setOpenReq(false)}
            onSubmit={async (dipId, dal, al, note) => {
              try {
                await apiFetch('/api/ferie/analisi/periodi', {
                  method: 'POST',
                  body: JSON.stringify({
                    dipendente_id: dipId,
                    dal,
                    al,
                    stato: 'richiesta',
                    anno_competenza: Number(dal.slice(0, 4)),
                    note: note || null,
                  }),
                });
                await refreshLiveData();
                setOpenReq(false);
                toast.success('Richiesta inviata');
              } catch (error) {
                toast.error(error.message || 'Invio non riuscito');
              }
            }}
          />
        </Dialog>
        </div>
      </div>

      <FerieAnalisi embedded refreshKey={analysisRefreshKey} />
    </div>
  );
}

function StatCol({ label, value, tone }) {
  const cls = tone === 'ok' ? 'text-brand-green' : tone === 'warn' ? 'text-amber-700' : 'text-stone-900';
  return (
    <div>
      <div className={`text-lg font-display font-semibold ${cls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">{label}</div>
    </div>
  );
}

function ListaPeriodi({ titolo, periodi, dipendentiInScope, azioni = [], saldoOf }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
      <div className="px-5 py-4 border-b border-stone-100">
        <div className="font-display font-semibold text-stone-900 tracking-tight">{titolo}</div>
        <div className="text-xs text-stone-500 mt-0.5">{periodi.length} periodi</div>
      </div>
      <div className="divide-y divide-stone-100">
        {periodi.length === 0 ? (
          <div className="p-6 text-sm text-stone-500 text-center">Nessun periodo.</div>
        ) : periodi.map(p => {
          const d = dipendentiInScope.find(x => x.id === p.dipendenteId);
          const s = saldoOf(p.dipendenteId);
          return (
            <div key={p.id} data-testid={`periodo-${p.id}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-stone-100 text-stone-800 font-display font-semibold flex items-center justify-center shrink-0">
                  {d?.nome[0]}{d?.cognome[0]}
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-stone-900 tracking-tight">{d?.nome} {d?.cognome}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-1.5">
                    <Calendar className="w-3 h-3"/> {p.dal} → {p.al} · {p.giorni}g · comp. {p.annoCompetenza}
                  </div>
                  {p.note && <div className="text-xs text-stone-500 mt-0.5 italic">&ldquo;{p.note}&rdquo;</div>}
                  {s && p.stato === 'richiesta' && (
                    <div className="text-[11px] text-stone-500 mt-1">
                      Saldo attuale: <b>{s.disponibile}g</b> → dopo: <b>{Math.round((s.disponibile - p.giorni)*100)/100}g</b>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <StatoBadge stato={p.stato}/>
                {azioni.map(a => (
                  <Button
                    key={a.key}
                    data-testid={`${a.testid}-${p.id}`}
                    size="sm"
                    variant={a.variant || 'default'}
                    className={`rounded-full gap-1 ${a.cls || ''}`}
                    onClick={() => a.fn(p.id)}
                  >
                    <a.icon className="w-3.5 h-3.5"/> {a.label}
                  </Button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RichiediFerieDialog({ defaultDipendenteId, forzaSelf, onClose, onSubmit }) {
  const { dipendentiInScope, previewRichiesta } = useApp();
  const [dipId, setDipId] = useState(defaultDipendenteId || dipendentiInScope[0]?.id || '');
  const [dal, setDal] = useState('');
  const [al, setAl]   = useState('');
  const [note, setNote] = useState('');

  const preview = dal && al && dipId ? previewRichiesta(dipId, dal, al) : null;

  const submit = () => {
    if (!dipId || !dal || !al) return toast.error('Compila tutti i campi obbligatori');
    if (new Date(al) < new Date(dal)) return toast.error('La data finale è precedente all\'iniziale');
    onSubmit(dipId, dal, al, note);
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="font-display tracking-tight">Nuova richiesta ferie</DialogTitle>
        <DialogDescription>Le richieste vengono valutate dal responsabile. Nessun blocco automatico salvo errore strutturale.</DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-stone-600">Dipendente</label>
          <Select value={dipId} onValueChange={setDipId} disabled={forzaSelf}>
            <SelectTrigger data-testid="req-select-dipendente" className="mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>
              {dipendentiInScope.map(d => <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-stone-600">Dal</label>
            <Input data-testid="req-dal" type="date" value={dal} onChange={(e) => setDal(e.target.value)} className="mt-1"/>
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-600">Al</label>
            <Input data-testid="req-al" type="date" value={al} onChange={(e) => setAl(e.target.value)} className="mt-1"/>
          </div>
        </div>
        {preview && (
          <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-stone-600">Giorni richiesti</span>
              <span className="font-semibold text-stone-900">{preview.giorni}g</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-stone-600">Saldo dopo</span>
              <span className={`font-semibold ${preview.saldoDopo < 0 ? 'text-red-600' : 'text-brand-green'}`}>{preview.saldoDopo}g</span>
            </div>
            {preview.avvisi.length > 0 && (
              <div className="space-y-1.5 pt-2 border-t border-stone-200">
                {preview.avvisi.map((a, i) => (
                  <div key={i} className={`text-xs flex items-start gap-1.5 ${a.tipo === 'error' ? 'text-red-700' : 'text-amber-800'}`}>
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0"/>{a.msg}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div>
          <label className="text-xs font-semibold text-stone-600">Note (opzionale)</label>
          <Textarea data-testid="req-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1"/>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>Annulla</Button>
        <Button data-testid="btn-invia-richiesta" onClick={submit} className="bg-brand-green hover:bg-brand-green-hover rounded-full">Invia</Button>
      </DialogFooter>
    </DialogContent>
  );
}
